import React, { useState, useEffect } from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import { actions, selectors } from '../../Redux/index';
import {i18n, i18nString} from '../../Utility/Format';
import {FormattedMessage} from 'react-intl';
import moment from 'moment';
import DrawerPanel, {
    DrawerPanelPanel,
    DrawerPanelPanelHeader,
    DrawerPanelPanelContent
} from '../../Components/Helper/DrawerPanel';
import PanelToggleButton from '../../Components/Controls/PanelToggleButton';
import Backdrop from '../../Components/Helper/Backdrop';
import Icon from '../../Components/Helper/Icon';
import CurrencyPairOptions from '../../Components/Options/CurrencyPairOptions';
import CurrencyHolidayIcon from '../../Components/Widgets/CurrencyHolidayIcon';
import Calendar from '../../Components/Widgets/Calendar';
import PanelHeader from '../../Components/Content/ApplicationHeader';

const ValueDateCalendar = (props) => {
    const {
        isOpen,
        onTogglePanel,
        getHolidays,
        holidays,
        localeName,
        currencies
    } = props;

    const [ccyPairs, setCcyPairs] = useState([{
        base: { isoString: "EUR", status: "BASE", label: "Euro", country: "EU" },
        counter: { isoString: "USD", status: "COUNTER", label: "US-Dollar", country: "USA" }
    }]);

    /*const fixCalendarARIA = () => {
        const dates = document.querySelectorAll('.rc-calendar-date');
        dates.forEach(el => {
            el.removeAttribute('role');
            el.removeAttribute('aria-selected');
            el.setAttribute('role', 'button');
            //el.setAttribute('tabindex', '0');
            if (!el.getAttribute('aria-label')) {
                el.setAttribute('aria-label', el.innerText);
            }
        });
    };

    useEffect(() => {
        const observer = new MutationObserver(fixCalendarARIA);
        observer.observe(document.body, { childList: true, subtree: true });
        fixCalendarARIA();
        return () => observer.disconnect();
    }, []); */

    useEffect(() => {
        const cp = ccyPairs[0];
        if (cp.base.isoString && cp.counter.isoString) {
            getHolidays([cp.base.isoString, cp.counter.isoString]);
        }
    }, [ccyPairs, getHolidays]);

    const changeCurrencyPair = (index, status, newValue) => {
        const newCcyPairs = [...ccyPairs];
        newCcyPairs[index][status] = newValue;
        setCcyPairs(newCcyPairs);
    };

    const renderPanelContent = () => {
        const baseCcy = ccyPairs[0].base.isoString;
        const counterCcy = ccyPairs[0].counter.isoString;

        // console.log(`[ValueDateCalendar] Rendering content for baseCcy: ${baseCcy}, counterCcy: ${counterCcy}`);
        // console.log(`[ValueDateCalendar] Initial holidays from props:`, holidays);

        let safeHolidays = holidays;
        if (safeHolidays && typeof safeHolidays.toJS === 'function') {
            safeHolidays = safeHolidays.toJS();
            // console.log(`[ValueDateCalendar] Holidays after toJS():`, safeHolidays);
        }
        if (typeof safeHolidays === 'string') {
            try { 
                safeHolidays = JSON.parse(safeHolidays); 
                // console.log(`[ValueDateCalendar] Holidays after JSON.parse():`, safeHolidays);
            } catch(e) {
                // console.error(`[ValueDateCalendar] Error parsing holidays string:`, e);
            }
        }
        while (safeHolidays && safeHolidays.data && !safeHolidays[baseCcy] && !safeHolidays[counterCcy]) {
            safeHolidays = safeHolidays.data;
        }
        // console.log(`[ValueDateCalendar] Holidays after unwrapping 'data':`, safeHolidays);

        let highlightsMap = {};
        if (safeHolidays) {
            [baseCcy, counterCcy].forEach((ccy, index) => {
                const ccyHolidays = safeHolidays[ccy];
                // console.log(`[ValueDateCalendar] Checking holidays for currency ${ccy}:`, ccyHolidays);
                if (Array.isArray(ccyHolidays)) {
                    ccyHolidays.forEach(date => {
                        if (!date) return;
                        const dateStr = typeof date === 'string' ? date.substring(0, 10) : date;
                        const mDate = moment(dateStr, 'YYYY-MM-DD', true);
                        if (mDate.isValid()) {
                            const dateKey = dateStr;
                            const colorKey = index === 0 ? 'base' : 'counter';
                            
                            if (highlightsMap[dateKey]) {
                                highlightsMap[dateKey].color = 'both';
                            } else {
                                highlightsMap[dateKey] = {
                                    date: mDate,
                                    color: colorKey
                                };
                            }
                        } else {
                            // console.warn(`[ValueDateCalendar] Invalid date format for ${ccy}:`, date, `(Parsed as: ${dateStr})`);
                        }
                    });
                } else if (ccyHolidays) {
                    // console.warn(`[ValueDateCalendar] Expected array for ${ccy} holidays, but got:`, typeof ccyHolidays, ccyHolidays);
                } else {
                    // console.log(`[ValueDateCalendar] No holidays found for ${ccy}`);
                }
            });
        } else {
            // console.warn(`[ValueDateCalendar] safeHolidays is falsy after transformations`);
        }
        
        let highlights = Object.keys(highlightsMap).map(key => highlightsMap[key]);
        // console.log(`[ValueDateCalendar] Final mapped highlights:`, highlights);

        return (
            <div>
                <section>
                    <span className="hint">{i18n('dbm.options.chooseCcyPairAndPeriod')}</span>
                    <br />
                    <br />
                    <CurrencyPairOptions
                        currencies={currencies}
                        currencyPairs={ccyPairs}
                        locale={localeName}
                        onChangeCurrencyPair={changeCurrencyPair}
                        showRemoveButton={false}
                    />
                </section>
                <section className="u-padding--forceCollapse">
                    <Calendar
                        showToday={false}
                        showDateInput={false}
                        disabledDate={() => true}
                        localeName={localeName}
                        highlights={highlights}
                    />
                </section>
                <section>
                    <CurrencyHolidayIcon baseCcy={baseCcy} />
                    <CurrencyHolidayIcon counterCcy={counterCcy} />
                    <CurrencyHolidayIcon baseCcy={baseCcy} counterCcy={counterCcy} />
                    <div className="u-clearer"></div>
                </section>
            </div>
        );
    };

    return (
        <DrawerPanel onClick={onTogglePanel} isOpen={isOpen} orientation="right">
            <DrawerPanelPanel orientation="right">
                <DrawerPanelPanelHeader>
                    <PanelHeader
                        left={<h2>{i18n('dbm.button.valueDateCalendar')}</h2>}
                        right={<PanelToggleButton icon="close" onClick={onTogglePanel} aria-label={i18nString('dbm.button.close', localeName)} />}
                    />
                </DrawerPanelPanelHeader>
                <DrawerPanelPanelContent>
                    {renderPanelContent()}
                </DrawerPanelPanelContent>
            </DrawerPanelPanel>
            <Backdrop onClick={onTogglePanel} />
        </DrawerPanel>
    );
};

const __ValueDateCalendarToggle = (props) => {
    const handleKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            props.onTogglePanel();
        }
    };
    return (
        <span
            onClick={props.onTogglePanel}
            className="value-date-button"
            role="button"
            onKeyDown={handleKeyDown}
            tabIndex="0"
            aria-label={i18nString('dbm.button.valueDateCalendar.toggle', props.localeName)}>
            <Icon name="calender" />
            <FormattedMessage id="dbm.button.valueDateCalendar" />
        </span>
    )
};

__ValueDateCalendarToggle.propTypes = {
    /**
     * Function to toggle the panel's visibility.
     */
    onTogglePanel: PropTypes.func.isRequired,
    /** The current locale string (e.g., 'en' or 'de'). */
    localeName: PropTypes.string,
};

const mapDispatchToProps = (dispatch) => {
    return {
        onTogglePanel: () => {
            dispatch(actions.UI.ValueDateCalendar.toggle());
        },
        getHolidays: (args) => {
            dispatch(actions.Data.Currency.requestRetrieveHolidays({ccy: args}));
        }
    }
};

const mapStateToProps = (state) => {
    return {
        isOpen: selectors.UI.ValueDateCalendar.isOpen(state),
        holidays: selectors.Data.Currency.getHolidays(state),
        localeName: selectors.UI.Locale.getLocale(state),
        currencies: selectors.Data.Currency.getCurrencies(state)
    };
};

const _ValueDateCalendar = connect(mapStateToProps, mapDispatchToProps)(ValueDateCalendar);
const _ValueDateCalendarToggle = connect(mapStateToProps, mapDispatchToProps)(__ValueDateCalendarToggle);

export default _ValueDateCalendar;
export {_ValueDateCalendarToggle as ValueDateCalendarToggle};